import { useEffect, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const unread = items.filter((i) => !i.read).length;

  const handleClick = async (n) => {
    setOpen(false);
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
      } catch {
        /* ignore */
      }
    }
    if (n.link) navigate(n.link);
    load();
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
    } catch {
      /* ignore */
    }
    load();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          data-testid="notification-bell"
        >
          <Bell className="h-4 w-4 text-slate-600 dark:text-slate-300" />
          {unread > 0 && (
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-slate-900" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="px-4 py-3 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
          <div>
            <div className="font-semibold text-sm">Notifications</div>
            <div className="text-xs text-slate-500">
              {unread > 0 ? `${unread} unread` : "All caught up"}
            </div>
          </div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" onClick={markAll} data-testid="mark-all-read">
              <CheckCheck className="h-3.5 w-3.5 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && (
            <div className="px-4 py-10 text-center text-xs text-slate-500">
              You're all caught up.
            </div>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition ${
                !n.read ? "bg-emerald-50/50 dark:bg-emerald-900/10" : ""
              }`}
              data-testid={`notification-${n.id}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && (
                  <span className="h-2 w-2 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{n.title}</div>
                  {n.body && (
                    <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                      {n.body}
                    </div>
                  )}
                  <div className="text-[10px] font-mono text-slate-400 mt-1">
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
